import { getAgencySettings } from "../queries/agencySettings";
import { useQuery } from "react-query";
import useNetworkHeaders from "./useNetworkHeaders";
import { useCallback } from "react";
import { addHours, subHours } from "date-fns";

export function useTimezone({ defaultToAgencyTimezone = true }: { defaultToAgencyTimezone?: boolean } = {}) {
  const networkHeaders = useNetworkHeaders();

  const { data, isLoading } = useQuery("agencySettings", getAgencySettings(networkHeaders), {
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });

  const clientTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const clientIsInAgencyTimezone = data?.timezoneInfo.timezone === clientTimezone;

  const agencyTimezoneInfo = data?.timezoneInfo;
  const startOfFiscalYear = data?.startOfFiscalYear;
  const daysOff = data?.daysOff.map((day: string) => new Date(day));
  const clientTimezoneInfo = {
    timezone: clientTimezone,
    utcOffset: new Date().getTimezoneOffset() / -60,
  };

  const defaultTimezoneInfo = defaultToAgencyTimezone
    ? (agencyTimezoneInfo ?? clientTimezoneInfo) // Fallback to client timezone if agency timezone is not set
    : clientTimezoneInfo;

  // Mostly used for legacy CRM/CE where dates were stored in agency time but with a wrong timezone of UTC.
  // You should not need to use this for any other products or any features with new data models!
  const adjustToAgencyTime = useCallback(
    (date: Date) => addHours(date, agencyTimezoneInfo?.utcOffset ?? 0),
    [agencyTimezoneInfo],
  );
  const adjustFromAgencyTime = useCallback(
    (date: Date) => subHours(date, agencyTimezoneInfo?.utcOffset ?? 0),
    [agencyTimezoneInfo],
  );

  return {
    agencyTimezoneInfo,
    clientTimezoneInfo,
    defaultTimezoneInfo,
    clientIsInAgencyTimezone,
    startOfFiscalYear,
    daysOff,
    isLoading,

    adjustToAgencyTime,
    adjustFromAgencyTime,
  };
}

export type TimezoneInfo = ReturnType<typeof useTimezone>;
